import { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Quiz de 3 passos que cruza o que o visitante quer na noite (o que × onde dorme
 * × clima) com o perfil de cada bar e recomenda 1 a 3 casas.
 * Eixos pensados a partir das dúvidas reais de quem procura a vida noturna da ilha:
 * tipo de programa × vila onde está hospedado × agito ou sossego.
 * Pontua usando os campos `tipo` e `vila` de cada bar. 100% client-side.
 *
 * O Cavalo Marinho entra como opção sintética (é um "Bar e Restaurante" que vive
 * em restaurantes.json): recomendá-lo leva para /restaurante/cavalo-marinho, sem
 * duplicar a ficha.
 */

type PoolItem = {
  handle: string;
  title: string;
  blurb: string;
  tipo: string;
  vila: string;
  musicaAoVivo: boolean;
  link: string;
};

type Answers = { oque?: string; vila?: string; clima?: string };

const STEPS = [
  {
    key: 'oque' as const,
    title: 'O que você quer da noite na ilha?',
    options: [
      { v: 'A', label: 'Petiscar e beber com vista / pé na areia', icon: '🍤' },
      { v: 'B', label: 'Música ao vivo, forró — dançar', icon: '🪗' },
      { v: 'C', label: 'Hambúrguer e cerveja artesanal', icon: '🍔' },
      { v: 'D', label: 'Jantar num clima de bar', icon: '🍝' },
    ],
  },
  {
    key: 'vila' as const,
    title: 'Onde você está hospedado?',
    options: [
      { v: 'encantadas', label: 'Encantadas', icon: '🏝️' },
      { v: 'nova-brasilia', label: 'Nova Brasília', icon: '🗼' },
      { v: 'tanto-faz', label: 'Tanto faz / ainda não decidi', icon: '🤷' },
    ],
  },
  {
    key: 'clima' as const,
    title: 'Que clima você procura?',
    options: [
      { v: 'agito', label: 'Agito, música e gente', icon: '🎶' },
      { v: 'tranquilo', label: 'Tranquilo, pra conversar e beber', icon: '🌙' },
    ],
  },
];

// o que o visitante quer → tipos-alvo (o primeiro é o "carro-chefe") + casas preferidas
const WANT: Record<string, { tipos: string[]; prefer: string[] }> = {
  A: { tipos: ['bar-restaurante'], prefer: ['barranco-bar-e-restaurante'] },
  B: { tipos: ['musica-forro', 'bar-restaurante'], prefer: ['forro-do-wando'] },
  C: { tipos: ['hamburgueria-cerveja'], prefer: ['madruga-burger-beer', 'ilha-do-malte-beer-burguer'] },
  D: { tipos: ['bar-restaurante'], prefer: ['stazione-bar-e-restaurante', 'cavalo-marinho'] },
};

const TIPO_ICON: Record<string, string> = {
  'hamburgueria-cerveja': '🍔',
  'bar-restaurante': '🍻',
  'musica-forro': '🪗',
};
const TIPO_CHIP: Record<string, string> = {
  'hamburgueria-cerveja': 'Hambúrguer & cerveja',
  'bar-restaurante': 'Bar e restaurante',
  'musica-forro': 'Música ao vivo & forró',
};
const VILA_LABEL: Record<string, string> = {
  encantadas: 'Encantadas',
  'nova-brasilia': 'Nova Brasília',
};

// Cavalo Marinho — opção sintética (Bar e Restaurante que fica em restaurantes.json)
const CAVALO: PoolItem = {
  handle: 'cavalo-marinho',
  title: 'Cavalo Marinho',
  blurb: 'Bar e restaurante em Encantadas que vira palco de música ao vivo (reggae, forró, MPB) e é famoso pela Melancia Atômica.',
  tipo: 'bar-restaurante',
  vila: 'encantadas',
  musicaAoVivo: true,
  link: '/restaurante/cavalo-marinho',
};

function buildPool(bares: any[]): PoolItem[] {
  const fromBares: PoolItem[] = (bares || []).map((b: any) => ({
    handle: b.handle,
    title: b.title,
    blurb: b.subtitle || b.description || '',
    tipo: b.tipo,
    vila: b.vila,
    musicaAoVivo: !!b.musicaAoVivo,
    link: `/bar/${b.handle}`,
  }));
  return [...fromBares, CAVALO];
}

function score(item: PoolItem, a: Answers): number {
  let s = 0;
  const want = WANT[a.oque || ''];
  // 1) tipo — define a "prateleira"
  if (want) {
    if (want.tipos.includes(item.tipo)) s += 4;
    if (want.tipos[0] === item.tipo) s += 1; // carro-chefe pesa um pouco mais
    if (want.prefer.includes(item.handle)) s += 3; // casa que melhor entrega o desejo
  }
  // 2) vila — só prioriza quando o visitante escolheu uma (tanto-faz é neutro)
  if (a.vila && a.vila !== 'tanto-faz') {
    if (item.vila === a.vila) s += 2;
    else s -= 2; // à noite, no escuro, atravessar de vila é difícil
  }
  // 3) clima — agito puxa música ao vivo; tranquilo puxa casa mais sossegada
  if (a.clima === 'agito') s += item.musicaAoVivo ? 2 : 0;
  if (a.clima === 'tranquilo') s += item.musicaAoVivo ? -1 : 1;
  return s;
}

function reasons(item: PoolItem, a: Answers): string[] {
  const out: string[] = [];
  if (TIPO_CHIP[item.tipo]) out.push(TIPO_CHIP[item.tipo]);
  if (a.vila && a.vila !== 'tanto-faz' && item.vila === a.vila) {
    out.push('Na vila onde você dorme');
  } else if (VILA_LABEL[item.vila]) {
    out.push(VILA_LABEL[item.vila]);
  }
  out.push(item.musicaAoVivo ? 'Música ao vivo' : 'Clima mais tranquilo');
  return [...new Set(out.filter(Boolean))].slice(0, 3);
}

export function BaresQuiz({ bares }: { bares: any[] }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [done, setDone] = useState(false);

  const pool = buildPool(bares);

  const pick = (key: string, v: string) => {
    const next = { ...answers, [key]: v };
    setAnswers(next);
    if (step < STEPS.length - 1) setStep(step + 1);
    else setDone(true);
  };
  const reset = () => {
    setAnswers({});
    setStep(0);
    setDone(false);
  };

  let results: PoolItem[] = [];
  if (done) {
    const ranked = pool
      .map(it => ({ it, s: score(it, answers) }))
      .sort((a, b) => b.s - a.s);
    const top = ranked[0]?.s ?? 0;
    results = ranked
      .filter(r => r.s >= top - 1.5 && r.s > 0)
      .slice(0, 3)
      .map(r => r.it);
  }

  return (
    <section
      className="mb-16 bg-teal-deep -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-12 rounded-2xl"
      id="quiz"
    >
      <div className="max-w-3xl mx-auto text-center">
        <span className="font-body text-sm text-sand-200 font-semibold uppercase tracking-wider">
          Teste rápido
        </span>
        <h2 className="font-display text-3xl sm:text-4xl text-white mt-1 mb-2">
          Qual bar combina com a sua noite?
        </h2>
        <p className="font-body text-sand-100/90 mb-8">
          Responda 3 perguntas e eu indico onde começar a beber (e comer) na ilha.
        </p>

        {!done ? (
          <div className="bg-white rounded-2xl p-6 sm:p-8 text-left shadow-lg">
            <div className="flex items-center gap-2 mb-5">
              {STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-coral' : 'bg-sand-200'}`}
                />
              ))}
            </div>
            <p className="font-body text-xs text-warm-gray mb-1">
              Passo {step + 1} de {STEPS.length}
            </p>
            <h3 className="font-display text-xl sm:text-2xl text-warm-dark mb-5">
              {STEPS[step].title}
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {STEPS[step].options.map(opt => (
                <button
                  key={opt.v}
                  onClick={() => pick(STEPS[step].key, opt.v)}
                  className="flex items-center gap-3 p-4 rounded-xl border border-sand-200 hover:border-coral hover:bg-coral/5 transition-all text-left"
                >
                  <span className="text-2xl flex-shrink-0" aria-hidden="true">
                    {opt.icon}
                  </span>
                  <span className="font-body font-medium text-warm-dark text-sm">{opt.label}</span>
                </button>
              ))}
            </div>
            {step > 0 && (
              <button
                onClick={() => setStep(step - 1)}
                className="mt-5 font-body text-sm text-warm-gray hover:text-warm-dark"
              >
                ← Voltar
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-6 sm:p-8 text-left shadow-lg">
            <p className="font-body text-sm text-coral font-semibold uppercase tracking-wider mb-1">
              {results.length > 1 ? 'Suas melhores pedidas' : 'O bar com a sua cara'}
            </p>
            <h3 className="font-display text-2xl text-warm-dark mb-5">
              {results.length
                ? `Encontrei ${results.length === 1 ? 'o lugar certo' : `${results.length} lugares`} pra sua noite`
                : 'Dê uma olhada nos bares abaixo'}
            </h3>
            <div className="space-y-4">
              {results.map((it, i) => (
                <div
                  key={it.handle}
                  className={`flex gap-4 p-4 rounded-xl border ${i === 0 ? 'border-coral/40 bg-coral/5' : 'border-sand-200'}`}
                >
                  <span className="text-4xl flex-shrink-0" aria-hidden="true">
                    {TIPO_ICON[it.tipo] || '🍻'}
                  </span>
                  <div className="flex-1 min-w-0">
                    {i === 0 && (
                      <span className="font-body text-[11px] font-bold text-coral uppercase tracking-wide">
                        ★ Melhor combinação
                      </span>
                    )}
                    <h4 className="font-display text-lg text-warm-dark leading-tight">{it.title}</h4>
                    <div className="flex flex-wrap gap-1.5 my-1.5">
                      {reasons(it, answers).map((r, j) => (
                        <span
                          key={j}
                          className="font-body text-[11px] bg-sand-100 text-warm-dark px-2 py-0.5 rounded-full"
                        >
                          {r}
                        </span>
                      ))}
                    </div>
                    <p className="font-body text-sm text-warm-gray leading-snug mb-1">{it.blurb}</p>
                    <Link
                      to={it.link}
                      className="font-body text-sm font-semibold text-teal-deep hover:underline"
                    >
                      Ver detalhes →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={reset}
              className="mt-6 font-body text-sm text-warm-gray hover:text-warm-dark"
            >
              ↻ Refazer o teste
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
